import type { Ref } from 'vue';

import { copyToClipboard } from '~/utils/clipboard';

const FEEDBACK_MS = 1500;

/**
 * The shared copy/share-a-link behaviour first built for the rewards referral card:
 * a flashed `copied`/`shared` flag per button, the native share sheet where the
 * browser has one with a clipboard fallback where it doesn't, and error logging.
 * Callers own the analytics and any toast — the return values say what happened.
 */
export const useShareLink = () => {
  const { $logErrorMessage } = useNuxtApp();

  // One flag per button. Sharing falls back to a clipboard write on browsers without a share
  // sheet, and the tick has to land on the button the user actually pressed.
  const copied = ref(false);
  const shared = ref(false);

  const flash = (flag: Ref<boolean>): void => {
    flag.value = true;
    setTimeout(() => {
      flag.value = false;
    }, FEEDBACK_MS);
  };

  /**
   * Resolves true when the clipboard write landed; failures are logged and resolve false.
   * Accepts a promise so callers that must resolve the link first (e.g. minting a share)
   * can still issue the clipboard write inside the click gesture — see copyToClipboard.
   */
  const copyLink = async (url: string | Promise<string>, logContext: string): Promise<boolean> => {
    try {
      await copyToClipboard(url);
      flash(copied);
      return true;
    }
    catch (error) {
      $logErrorMessage(`${logContext}: clipboard copy failed`, { error });
      return false;
    }
  };

  /**
   * The native share sheet where the browser has one (every mobile browser, Safari desktop), and a
   * clipboard write where it doesn't (Firefox, Chrome desktop) — the button stays useful either way.
   * Resolves with the method that succeeded, or null when nothing did (user abort included).
   */
  const shareLink = async (
    url: string,
    share: { title: string; text?: string },
    logContext: string,
  ): Promise<'native' | 'clipboard' | null> => {
    if (import.meta.client && typeof navigator.share === 'function') {
      try {
        await navigator.share({ ...share, url });
        return 'native';
      }
      catch (error) {
        // An abort is the user closing the sheet, not a failure worth logging. Anything else —
        // a blocked permission, a rejected payload — is a real failure and gets logged.
        if (!(error instanceof Error && error.name === 'AbortError')) {
          $logErrorMessage(`${logContext}: share failed`, { error });
        }
        return null;
      }
    }

    try {
      await copyToClipboard(url);
      flash(shared);
      return 'clipboard';
    }
    catch (error) {
      $logErrorMessage(`${logContext}: share fallback copy failed`, { error });
      return null;
    }
  };

  return { copied, shared, copyLink, shareLink };
};
